import React, { useState, Fragment } from "react";
import HoverIconButton from "components/HoverIconButton";
import classnames from "classnames";
import { useFormikContext } from "formik";
import Modal from "components/Modal";
import ModalContent from "components/ModalContent";
import ModalActions from "components/ModalActions";
import Grip from "components/Grip";
import {
  ROW_MIN_HEIGHT,
  STATUS,
  TOOLTIP_POSITION,
  UI_THEME
} from "utils/constants/ui";
import { usePageBuilderContext } from "utils/context";
import Analytics, { REMOVE_PRODUCT_FROM_TABLE } from "utils/analytics";
import get from "lodash/get";
import Pill from "components/Pill";
import { pluralizeCopy } from "utils/copy";
import { STATE_KEYS } from "utils/constants/state";
import { getIconForUID } from "components/FormerEditor/utils";
import { COLORS } from "utils/styles";
import { removeManifestFormFieldGroup } from "utils/manifest";
import { getFieldGroupRemovalSlides } from "utils/constants/confirm";
import { useFirstComboFromFields } from "utils/hooks/manifest";

const DEFAULT_GROUP_TITLE = "Group";

const FieldGroupListItem = ({
  index,
  // manifest.data.content.33536f12-a173-42e8-bf12-adc3700db801
  name,
  title,
  subtitle,
  images,
  collapseImages,
  canRemove,
  draggable,
  canEmphasize,
  onClick,
  snapshot
}) => {
  const pageContext = usePageBuilderContext();
  const tracking = pageContext ? pageContext.tracking : {};
  const [showModal, setShowModal] = useState(false);
  const [hover, setHover] = useState();

  const ctxTitle = title || DEFAULT_GROUP_TITLE;

  const { values, touched, setValues, setTouched } = useFormikContext();
  const fieldGroupsPath = `${name}.${STATE_KEYS.FORM.FIELD_GROUPS}`;
  const fieldGroupPath = `${fieldGroupsPath}[${index}]`;
  const fieldGroupFieldsPath = `${fieldGroupPath}.fields`;
  const fields = get(values, fieldGroupFieldsPath) || [];
  const combo = useFirstComboFromFields(fields);

  const containerProps = {
    className: classnames(
      "flex flex-row justify-between items-center ph3 pv2 bh--inset relative overflow-hidden",
      {
        "pointer": draggable,
        "bg-content--secondary": hover,
        "bg-white": !hover
      }
    ),
    onMouseEnter: () => !hover && draggable && setHover(true),
    onMouseLeave: () => hover && draggable && setHover(false)
  };

  let action;
  if (canRemove) {
    action = (
      <HoverIconButton
        type="close"
        tooltip="Remove"
        tooltipPosition={TOOLTIP_POSITION.RIGHT}
        onClick={() => {
          Analytics.track(REMOVE_PRODUCT_FROM_TABLE, {
            ...tracking
          });
          setShowModal(true);
        }}
      />
    );
  }

  const bottomBorderAccent = (
    <div className="ph3">
      <div className="bb hairline-1 w-100"></div>
    </div>
  );

  const Icon = getIconForUID("fieldGroup");

  return (
    <Fragment>
      <div {...containerProps}>
        {draggable && <Grip hover={hover} snapshot={snapshot} />}
        <div className="flex flex-column w-100">
          <div className="flex flex-row w-100" style={ROW_MIN_HEIGHT}>
            <div
              onClick={onClick}
              className="w-100 flex flex-column items-start justify-center"
            >
              <div className="flex flex-row items-center">
                <div
                  style={{ height: 24, width: 24 }}
                  className={
                    "flex items-center justify-center bg-content--secondary br2 icon--boundary flex-shrink-0"
                  }
                >
                  <Icon size={16} color={COLORS.white} />
                  <style jsx>{`
                    .icon--boundary {
                      background-color: ${COLORS.primary};
                    }
                  `}</style>
                </div>

                <div
                  className={classnames(
                    "f6 fw5 w-100 link__underline pointer pl2",
                    {
                      pl3: canEmphasize
                    }
                  )}
                >
                  {ctxTitle}
                </div>
              </div>
            </div>
            <div className="flex items-start">{action}</div>
          </div>
          <div className="flex justify-end pt2">
            <Pill
              copy={pluralizeCopy("field", fields.length)}
              status={!fields.length ? STATUS.WARNING : STATUS.SUCCESS}
              theme={UI_THEME.SLIM}
            />
          </div>
        </div>
      </div>
      {showModal && (
        <Modal showModal={showModal} onClose={() => setShowModal(false)}>
          <ModalContent slides={getFieldGroupRemovalSlides(combo)}>
            <ModalActions
              actions={[
                {
                  cta: "Cancel",
                  onClick: () => {
                    setShowModal(false);
                  }
                },
                {
                  cta: "Yes",
                  onClick: () => {
                    removeManifestFormFieldGroup({
                      values,
                      touched,
                      setValues,
                      setTouched,
                      name: fieldGroupPath
                    });
                  }
                }
              ]}
            />
          </ModalContent>
        </Modal>
      )}
      {bottomBorderAccent}
    </Fragment>
  );
};

export default FieldGroupListItem;
